import { get, isPlainObject } from 'lodash-es';
import type { ModalFormPayload } from '@mts/components/Base/src/BaseModalForm.vue';
import type { ModalTablePayload } from '@mts/components/Base/src/BaseModalTable.vue';
import type { BasicColumn } from '@mts/components/Table';
import type { ActionContext, ActionHandlerFn, FieldMapping } from '@mts/render';
import type { ColumnType } from 'ant-design-vue/es/table';
import { type LocaleType, mitt, UI_RATIO } from '@mts-fe/utils';
import type { CarrierType } from '@/views/common/global/CarrierModal.vue';
import { PADMode } from './env';

export * from './MTSApp';

/** 业务侧通讯总线 */
export const emitter = mitt<{
  /** 显示载具详情 */
  showCarrierModal: {
    /**
     * @link https://alidocs.dingtalk.com/i/nodes/amweZ92PV6epl4GXtZaX92ArWxEKBD6p?cid=29655228:540868603&corpId=ding74f35c7f8260c971acaaa37764f94726&iframeQuery=utm_medium=im_card&utm_source=im&utm_medium=main_vertical&utm_scene=team_space&utm_source=search
     */
    bizCode?: string;
    itemType?: CarrierType;
    carrierNo?: string;
    [x: string]: any;
  };
  [x: string]: any;
}>();

/** 通讯总线：事件发布订阅 */
export const mtsEmitter = mitt<{
  /** 多语言切换 */
  localeChanged: LocaleType;
  /** 表格开始发请求 */
  tableFetch: Recordable;
  /** 表格内部属性变更 */
  tableInnerChanged: boolean;
  /** 表格列宽修改 */
  tableWidthChanged: ColumnType<BasicColumn>;
  /** 开启异步导出任务 */
  startExportTask: ActionContext & { exportParams: Recordable };
  /** 关闭异步任务执行弹窗 */
  hideAsyncTask: Recordable;
  /** 打开异步任务记录弹窗 */
  showAsyncTaskRecord: ActionContext;
  /** 打开审核弹窗 */
  showApprovalFlow: { actionHandler?: ActionHandlerFn; record?: Recordable };
  /** 打开审核记录弹窗 */
  showApprovalRecord: ActionContext;
  /** 打开全局表单弹窗 */
  showModalForm: ModalFormPayload;
  /** 打开全局表格弹窗 */
  showModalTable: ModalTablePayload;
  /** 打开全局表格弹窗 */
  showModalCard: ModalTablePayload;
  /** 扫码枪 */
  scannerReadCode: { code: string; deviceName: string };
  /** 扫码枪：上架位绑定 */
  'scannerReadCode:upBind': string;
  /** 批量读取 EPC（取第一个使用） */
  readMoreEPC: string[];
}>();

// 事件监听示例
// mtsEmitter.on('localeChanged', (data) => {
//   console.log('localeChanged', data);
// });

/** 多字段文本拼接 */
export const concatText = (textList: string[], concatBy = '｜') =>
  textList.filter(Boolean).join(concatBy);

/** 尺码对象转字符串 */
export const convertSizeMap = (obj: Recordable) =>
  Object.entries(obj)
    .map(([key, value]) => `${key}:${value}`)
    .join(' ');

/** 清除焦点 */
export const clearFocus = () => {
  (document.activeElement as HTMLElement)?.blur?.();
};

/**
 * 数据对象键值转换
 * @description VOS -> Params，VO -> Param
 * @param object object
 * @returns object
 */
export function objectKeyVo2Param(object: Recordable) {
  const data: Recordable = {};

  for (const key in object) {
    const _key = key.replace('VOS', 'Params').replace('VO', 'Param');
    data[_key] = object[key];

    if (Array.isArray(data[_key])) {
      data[_key] = data[_key].map((it, i) => {
        if (isPlainObject(it)) {
          it.sort = i;
          return objectKeyVo2Param(it);
        }
        return it;
      });
    } else if (isPlainObject(data[_key])) {
      data[_key] = objectKeyVo2Param(data[_key]);
    }
  }
  return data;
}

/** 初始化时窗口尺寸宽高 */
export const windowSize = {
  width: window.innerWidth,
  height: window.innerHeight,
};

/** 基于UI标准和当前视口大小自适应 */
export const adaptiveSize = (pixels: number, basedOnHeight = false) => {
  if (!PADMode) return pixels;

  const ratio = basedOnHeight ? UI_RATIO.H : UI_RATIO.W;
  return ratio * pixels;
};

/** 根据视口已使用高度，获取可滚动高度 */
export const getScrollHeight = (usedHeight?: number) =>
  typeof usedHeight === 'number' ? windowSize.height - adaptiveSize(usedHeight) : usedHeight;

const widthMap = PADMode
  ? { small: adaptiveSize(520), middle: adaptiveSize(623), large: adaptiveSize(1200) }
  : { small: 520, middle: 1200, large: 1520 };
export type ModalSize = 'small' | 'middle' | 'large' | 'full';
/** 获取标准弹窗尺寸 */
export const getModalSize = (modalSize: ModalSize = 'middle', fixed = true) => {
  if (modalSize === 'full') {
    return { defaultFullscreen: true };
  }

  if (PADMode) {
    const ratioMap = { small: 0.5, middle: 0.8, large: 0.9 };
    return {
      width: widthMap[modalSize],
      [fixed ? 'height' : 'minHeight']: ratioMap[modalSize] * (windowSize.height - 141),
    };
  }

  const ratioMap = fixed
    ? { small: 0.45, middle: 0.75, large: 0.95 }
    : { small: 0.3, middle: 0.45, large: 0.6 };
  let height = ratioMap[modalSize] * (windowSize.height - 110);
  if (fixed) {
    const heightLimit = { small: 380, middle: 520, large: 660 }[modalSize];
    height < heightLimit && (height = heightLimit);
  }
  return {
    width: widthMap[modalSize],
    [fixed ? 'height' : 'minHeight']: height,
  };
};

/** 获取字段映射 */
export const getMappingData = (mapping: FieldMapping[], record: Recordable = {}) =>
  mapping.reduce((acc, cur) => ({ ...acc, [cur.valueField]: get(record, cur.metaField) }), {});

export const getConfigEditPath = (template: string, pageCode = 'add', areaId = '') => {
  const pathname =
    { COMPOSITE_PAGE: 'composite', PDA_PAGE: 'pdaConfig', CUSTOM: 'block' }[template] || 'config';
  return `containerConfig/${pathname}/${pageCode}?template=${template || ''}&areaId=${areaId}`;
};

/** 注册全局方法 $mts */
export const registeGlobalParam = (param: Recordable) => {
  !window.$mts && (window.$mts = {});
  Object.assign(window.$mts, param);
};

/** 常用后端微服务映射 */
export const SERVICE_PREFIX = {
  LC: '/low-code',
  LCA: '/low-code-auth',
  LCC: '/low-code/lowCode/config',
  LCP: '/low-code/public',
  LCIC: '/low-code/ims/config',
  ISYS: '/ims-system',
  IA: '/ims-auth',
  ICD: '/ims-core-data',
  ISALES: '/ims-sales',
  IP: '/ims-purchase',
  IPC: '/ims-production-core',
  IW: '/ims-warehouse',
  IWC: '/ims-warehouse-core',
  IRC: '/ims-report-core',
  ILOG: '/ims-log',
  MP: '/mes-production',
  MPS: '/mes-production-sewing',
  WW: '/wms-warehouse',
  WIW: '/wms-intelligent-warehouse',
  SGS: '/sample-garment-sorting',
} as const;

/** 可用后端微服务 */
export const BACKEND_SERVICES = [
  'low-code',
  'ims-system',
  'ims-auth',
  'ims-core-data',
  'ims-sales',
  'ims-purchase',
  'ims-production-core',
  'ims-warehouse',
  'ims-warehouse-core',
  'ims-report-core',
  'ims-log',
  'mes-production',
  'mes-production-sewing',
  'wms-warehouse',
  'wms-intelligent-warehouse',
  'sample-garment-sorting',
  'low-code-auth',
] as const;

const OFFSET_HEIGHT_DEPTH = {
  /** 窗口尺寸 */
  WINDOW: 0,
  /** 顶部栏高度 */
  HEADER: 48,
  /** 顶部栏高度 + 标题栏高度 */
  CONTENT: 48 + 48,
};
/** 实时获取偏移高度 */
export const getOffsetHeight = (
  offset: number,
  offsetBy: keyof typeof OFFSET_HEIGHT_DEPTH = 'WINDOW',
) => window.innerHeight - OFFSET_HEIGHT_DEPTH[offsetBy] - offset;

export function warn(message: string, ...args: any[]) {
  console.warn(`[MTS warn]:${message}`, ...args);
}
export function error(message: string, ...args: any[]) {
  throw new Error(`[MTS error]:${message}`, ...args);
}
